import React, { useContext, useEffect } from 'react';

import PropTypes from 'prop-types';

import CustomIcon from '../Elements/CustomIcon';
import Label from '../Elements/Label';
import Select from '../Elements/Select';

import CountrySelector from '../Elements/CountrySelector';
import TextArea from '../Elements/TextArea';
import Span from '../Elements/Span';
import Markdown from '../Elements/Markdown';
import { Context } from '../Wizard/WizardWrapper';
import InputText from '../Elements/InputText';
import InputEmail from '../Elements/InputEmail';
import { stringUcFirst } from '../../../../utils/stringUcFirst';
import ButtonIcon from '../Elements/ButtonIcon';
import { useRefereeValidation } from '../../hooks/useRefereeValidation';
import { useStaticCollections } from '../../../../context/StaticCollections';

const IndependentAssessor = ({
  item,
  className,
  classNameMarginTop,
  fieldsValidated,
}) => {
  const {
    setValue,
    watch,
    errors,
    trigger,
    setError,
    clearErrors,
    refereeReportData,
  } = useContext(Context);
  const watchIsFellow = watch(item.name);
  const watchFellowId = watch(`${item.name}FellowId`);
  const watchNonAasRefereeId = watch(`nonAas${stringUcFirst(item.name)}Id`);
  const { fellowsArray, nonAasRefereesArray } = useStaticCollections();

  const candidateName = refereeReportData?.candidateName;
  const sectionalCommitteeName = refereeReportData?.sectionalCommitteeName;
  const isRedirecting = refereeReportData?.isRedirecting || false;

  const firstName = watch(`${item.name}Name`);
  const lastName = watch(`${item.name}LastName`);
  const enteredEmail = watch(`${item.name}Email`);
  const educationalInstitution = watch(
    `${item.name}EmailEducationalInstitution`,
  );

  useRefereeValidation({
    fieldPrefix: item.name,
    firstName,
    lastName,
    email: enteredEmail,
    fellowsArray,
    nonAasRefereesArray,
    errors,
    setError,
    clearErrors,
    enabled: !isRedirecting,
  });

  useEffect(() => {
    const nonAasRefereeFields = [
      'TitleId',
      'Name',
      'LastName',
      'Email',
      'CountryId',
      'Memberships',
      'Organisation',
      'EmailEducationalInstitution',
    ];

    const clearNonAasFields = () => {
      nonAasRefereeFields.forEach((fieldName) => {
        const fullFieldName = `${item.name}${fieldName}`;
        if (fieldName === 'TitleId') {
          setValue(fullFieldName, undefined, { shouldValidate: false });
        } else if (fieldName === 'CountryId') {
          setValue(fullFieldName, null, { shouldValidate: false });
        } else {
          setValue(fullFieldName, '', { shouldValidate: false });
        }
      });
    };

    const revalidateNonAasFields = () => {
      const fieldsToRevalidate = [
        `nonAas${stringUcFirst(item.name)}Id`,
        ...nonAasRefereeFields.map((f) => `${item.name}${f}`),
      ];
      trigger(fieldsToRevalidate);
    };

    if (parseInt(watchIsFellow) === 1) {
      trigger(`nonAas${stringUcFirst(item.name)}Id`);

      if (watchNonAasRefereeId) {
        setValue(`nonAas${stringUcFirst(item.name)}Id`, null, {
          shouldValidate: false,
        });
        clearNonAasFields();
        revalidateNonAasFields();
      }

      trigger(`${item.name}FellowId`);
    }

    if (parseInt(watchIsFellow) === 0) {
      trigger(`${item.name}FellowId`);

      if (watchFellowId) {
        setValue(`${item.name}FellowId`, null, { shouldValidate: false });
      }

      trigger(`nonAas${stringUcFirst(item.name)}Id`);

      if (watchNonAasRefereeId && parseInt(watchNonAasRefereeId.value) === 0) {
        nonAasRefereeFields.forEach((fieldName) => {
          trigger(`${item.name}${fieldName}`);
        });
      }

      if (watchNonAasRefereeId && parseInt(watchNonAasRefereeId.value) > 0) {
        clearNonAasFields();
        revalidateNonAasFields();
      }
    }
  }, [
    item.name,
    setValue,
    trigger,
    watchIsFellow,
    watchFellowId,
    watchNonAasRefereeId,
  ]);

  const [refereeSevenTitleId, refereeEightTitleId, refereeNineTitleId] = watch([
    'refereeSevenTitleId',
    'refereeEightTitleId',
    'refereeNineTitleId',
  ]);

  useEffect(() => {
    trigger('refereeSevenTitleId');
    trigger('refereeEightTitleId');
    trigger('refereeNineTitleId');
  }, [refereeSevenTitleId, refereeEightTitleId, refereeNineTitleId, trigger]);

  useEffect(() => {
    trigger(`${item.name}Organisation`);
  }, [educationalInstitution, item.name, trigger]);

  const classNameMarginBottom = Number.isInteger(parseInt(watch(item.name)))
    ? ''
    : 'mb-lg-5';

  const nonAasRefereeKey = `nonAas${stringUcFirst(item.name)}Id`;
  return (
    <React.Fragment key={`referee-field-collection-${item.name}`}>
      {item.name !== null && item.name ? (
        <h1 className="mb-xl-3">
          {candidateName}{' '}
          {sectionalCommitteeName && (
            <span className="badge badge-info badge-small">
              {sectionalCommitteeName}
            </span>
          )}
        </h1>
      ) : null}
      <Markdown
        className="col-xl-12 col-lg-12 card-header"
        text={
          item.label !== null && item.label !== undefined
            ? `**Independent Assessor ${item.label.match(/\d+/g)[0]}**`
            : '**Independent Assessor**'
        }
      />
      <div
        className={`${className} ${classNameMarginTop} ${classNameMarginBottom}`}
      >
        <div className="form-group">
          <Label
            className="mb-3"
            label={item.label}
            name={item.name}
            errors={errors}
            required={item.required}
          />

          <CustomIcon
            fieldsValidated={fieldsValidated}
            fieldError={errors[item.name]}
            required={item.required}
          />
          {!item.required ? (
            <ButtonIcon
              bootstrapIcon="bi-arrow-counterclockwise"
              backgroundColor="bg-success"
              handleClick={() => {
                setValue(item.name, '');
              }}
              handleKeyPress={() => {
                setValue(item.name, '');
              }}
              tooltipText="Reset field"
            />
          ) : null}
          <Select
            options="binaries"
            name={item.name}
            onChange={(e) => setValue(item.name, parseInt(e.target.value))}
          />

          <Span text={errors[item.name] ? errors[item.name].message : ''} />
        </div>

        <Markdown
          className="col-xl-12 col-lg-12 mt-3"
          text={item.description}
        />
      </div>

      <div />

      {parseInt(watch(item.name)) === 0 ? (
        <div
          className={`${className} ${classNameMarginTop} ${watchNonAasRefereeId && watchNonAasRefereeId.value === 0 ? '' : 'mb-lg-5'}`}
        >
          <div className="form-group">
            <Label
              className="mb-3"
              label="Select existing non AAS referee"
              name={nonAasRefereeKey}
              errors={errors}
              required={item.required}
            />

            <CustomIcon
              fieldsValidated={fieldsValidated}
              fieldError={errors[nonAasRefereeKey]}
              required={item.required}
            />

            <Select options="non-aas-referees" name={nonAasRefereeKey} />

            {errors[nonAasRefereeKey] ? (
              <Span text={errors[nonAasRefereeKey].message} />
            ) : (
              ''
            )}

            <div className="col-xl-12 col-lg-12 mt-3">
              <p>
                If not listed, scroll to the bottom to select -- My non AAS
                referee is not listed --
              </p>
              <p>
                Please contact{' '}
                <a href="mailto:fellowship@science.org.au">
                  fellowship@science.org.au
                </a>{' '}
                if a listed referee's email address has changed.
              </p>
            </div>
          </div>

          <Markdown
            className="col-xl-12 col-lg-12 mt-3"
            text={item.description}
          />
        </div>
      ) : (
        ''
      )}

      {parseInt(watch(`nonAas${stringUcFirst(item.name)}Id`)?.value) === 0 ? (
        <>
          <div className={`${className}  ${classNameMarginBottom}`}>
            <div className="form-group mt-2">
              <Label
                className="mt-5"
                label="Title:"
                name={`${item.name}TitleId`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}TitleId`]}
                required={item.required}
              />

              <Select options="titles" name={`${item.name}TitleId`} />

              {errors[`${item.name}TitleId`] ? (
                <Span text={errors[`${item.name}TitleId`].message} />
              ) : (
                ''
              )}
            </div>
          </div>
          <div className={`${className} ${classNameMarginTop}`}>
            <div className="form-group mt-2">
              <Label
                label="Given Name"
                name={`${item.name}Name`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}Name`]}
                required={item.required}
              />

              <InputText name={`${item.name}Name`} />

              {errors[`${item.name}Name`] ? (
                <Span text={errors[`${item.name}Name`].message} />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className={`${className} ${classNameMarginTop}`}>
            <div className="form-group mt-2">
              <Label
                label="Last Name"
                name={`${item.name}LastName`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}LastName`]}
                required={item.required}
              />

              <InputText name={`${item.name}LastName`} />

              {errors[`${item.name}LastName`] ? (
                <Span text={errors[`${item.name}LastName`].message} />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className={`${className} ${classNameMarginTop}`}>
            <div className="form-group mt-2">
              <Label
                label="Email"
                name={`${item.name}Email`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}Email`]}
                required={item.required}
              />

              <InputEmail name={`${item.name}Email`} />

              {errors[`${item.name}Email`] ? (
                <Span text={errors[`${item.name}Email`].message} />
              ) : (
                ''
              )}

              {!watch(`${item.name}EmailEducationalInstitution`) &&
              watch(`${item.name}Email`) ? (
                <div>
                  <Label
                    className="mt-3"
                    label="Institution / organisation"
                    name={`${item.name}Organisation`}
                    errors={errors}
                    required={item.required}
                  />
                  <InputText name={`${item.name}Organisation`} />
                  {errors[`${item.name}Organisation`] ? (
                    <Span text={errors[`${item.name}Organisation`].message} />
                  ) : (
                    ''
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className={`${className} ${classNameMarginTop}`}>
            <div className="form-group mt-2">
              <Label
                label="Country"
                name={`${item.name}CountryId`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}CountryId`]}
                required={item.required}
              />

              <CountrySelector name={`${item.name}CountryId`} />

              {errors[`${item.name}CountryId`] ? (
                <Span text={errors[`${item.name}CountryId`].message} />
              ) : (
                ''
              )}
            </div>
          </div>

          <div className={`col-lg-12 ${classNameMarginTop}`}>
            <div className="form-group mt-3 mb-xl-5">
              <Label
                label="Academy membership/s (or equivalent)"
                name={`${item.name}Memberships`}
                errors={errors}
                required={item.required}
              />

              <CustomIcon
                fieldsValidated={fieldsValidated}
                fieldError={errors[`${item.name}Memberships`]}
                required={item.required}
              />

              <Markdown
                key="elements-description-markdown-1"
                className="col-xl-12 col-lg-12"
                text="eg., FAA FTSE FASc FMRS"
              />

              <TextArea
                name={`${item.name}Memberships`}
                className={className}
              />

              {errors[`${item.name}Memberships`] ? (
                <Span text={errors[`${item.name}Memberships`].message} />
              ) : (
                ''
              )}
            </div>
          </div>
        </>
      ) : (
        ''
      )}

      {parseInt(watch(item.name)) === 1 ? (
        <div className={`${className} ${classNameMarginTop} mb-lg-5`}>
          <div className="form-group">
            <Label
              className="mb-3"
              label="Select existing AAS fellow"
              name={`${item.name}FellowId`}
              errors={errors}
              required={item.required}
            />

            <CustomIcon
              fieldsValidated={fieldsValidated}
              fieldError={errors[`${item.name}FellowId`]}
              required={item.required}
            />

            <Select options="fellows" name={`${item.name}FellowId`} />

            {errors[`${item.name}FellowId`] ? (
              <Span text={errors[`${item.name}FellowId`].message} />
            ) : (
              ''
            )}
          </div>

          <Markdown
            className="col-xl-12 col-lg-12 mt-3"
            text={item.description}
          />
        </div>
      ) : (
        ''
      )}
    </React.Fragment>
  );
};

IndependentAssessor.propTypes = {
  item: PropTypes.object,
  className: PropTypes.string,
  classNameMarginTop: PropTypes.string,
  fieldsValidated: PropTypes.bool,
};

export default IndependentAssessor;
